import {Component, Input, OnInit, ViewChild} from '@angular/core';
import {QuestionComponent} from '../question.component';
import {ImageManipulationService, IResizeImageOptions} from '../image-manipulation.service';
import {ImageAsset, ImageAssetFactory, ImageAssetType} from '../../domain/card';
import {NgxSpinnerService} from 'ngx-spinner';
import {ScrollService} from '../scroll.service';
import {FormGroup} from '@angular/forms';

@Component({
  selector: `crds-question-multi-image-upload`,
  template: `
    <div *ngIf="show" [formGroup]="form">
      <label *ngIf="question.label" class="form-label" [attr.for]="question.key">{{question.label}}:
        <span *ngIf="question.required"> *</span>
        <crds-form-error *ngIf="isInvalid" [question]="question"
                         [message]="question.errorMessage"></crds-form-error>
      </label>

      <!-- QUESTION INPUT -->
      <input [id]="question.key"
             [name]="question.key"
             (change)="setFromImage($event)"
             class="imageCapture"
             style="display:none;"
             type="file"
             accept="image/*"/>
      <button (click)="clickFile(question.key)"
              type="button"
              class="btn btn-primary image-upload-button"
              #imageUploadButton>
        <span>{{hasUploadedImages ? 'Add' : 'Upload'}} image</span>
        <i class="material-icons button-icon">{{hasUploadedImages ? 'add_a_photo' : 'photo_camera'}}</i>
      </button>

      <!-- QUESTION EXAMPLE IMAGE OR USER UPLOADED -->
      <div class="image-wrapper">
        <crds-question-hint *ngIf="!hasUploadedImages" [question]="question"></crds-question-hint>
        <div *ngIf="!hasUploadedImages && images && images.length">
          <crds-image-asset [images]="images" [key]="question.key"></crds-image-asset>
        </div>
        <div *ngIf="hasUploadedImages">
          <div class="row" *ngFor="let uploadedImage of value; index as i;">
            <div class="col col-9">
              <img [id]="i + '_temp'"
                   [src]="uploadedImage"/>
            </div>
            <div class="col col-3 pt-5">
              <button (click)="clearImage(i)"
                      type="button"
                      class="btn btn-primary image-upload-button"
                      #imageUploadButton>
                <i class="material-icons">delete_forever</i>
              </button>
            </div>
          </div>
        </div>
      </div>
      <ngx-spinner
        bdColor="rgba(51, 51, 51, 0.8)"
        size="large"
        type="line-scale">
      </ngx-spinner>
    </div>`,
  styles: [
    `
      .material-icons {
        color: #ffffff;
      }
      .image-upload-button > span {
        line-height: 2rem;
        padding-right: 0.5rem;
        vertical-align: middle;
      }
      .image-upload-button > .material-icons {
        vertical-align: middle;
      }
    `
  ]
})
export class MultiImageUploadQuestionComponent extends QuestionComponent implements OnInit {

  @Input() form: FormGroup;
  @Input() question;
  @ViewChild('imageUploadButton') input;
  type: string;
  images: ImageAsset[];
  imageAssetType = ImageAssetType;

  constructor(private spinnerService: NgxSpinnerService,
              private imageService: ImageManipulationService,
              private imageAssetFactory: ImageAssetFactory,
              private scrollService: ScrollService) {
    super();
  }

  ngOnInit() {
    /**
     * Check for a non-array value
     */
    if (!(this.value instanceof Array)) {
      setTimeout(() => {
        this.form.get(this.question.key).patchValue([this.value]);
      });
    }
    this.images = this.imageAssetFactory.create(this.question.image, this.form);
    this.scrollService.scrollObservable
      .subscribe((documentId) => {
        if (documentId === this.question.key) {
          this.input.nativeElement.scrollIntoView();
        }
      });
  }

  get isInvalid() {
    return this.form.get(this.question.key).touched && this.form.get(this.question.key).invalid;
  }

  get show() {
    const showIfQuestionPopulated = this.question.showIf ?
      this.showIf :
      true;
    return (this.question.showIfPopulated !== true || (this.value)) &&
      showIfQuestionPopulated && this.question.show !== false;
  }

  get hasUploadedImages() {
    return this.value instanceof Array && this.value.length;
  }

  clearImage(index: number) {
    const formValue = this.value;
    formValue.splice(index, 1);
    this.form.get(this.question.key).patchValue(formValue);
  }

  clickFile(questionKey) {
    (document.getElementById(questionKey) as HTMLElement).click();
  }

  setFromImage(event) {
    this.spinnerService.show();
    const file: File = event.target.files[0];
    const maxSize = 1000;
    let orientationTemp = 1;

    return this.imageService.resetOrientation(file)
      .then((reorientationResult: { result: string, orientation: number }) => {
        orientationTemp = reorientationResult.orientation;
        const options: IResizeImageOptions = {
          maxSize: maxSize,
          b64: reorientationResult.result
        };
        this.imageService.resizeImage(options)
          .then((resizedImage) => {
            const formValue = this.value;
            if (this.question.sort && this.question.sort === 'desc') {
              formValue.unshift(resizedImage);
            } else {
              formValue.push(resizedImage);
            }
            this.form.get(this.question.key).patchValue(formValue);
            this.spinnerService.hide();
          });
      })
      .catch((error) => {
        // Swallowing error as likely comes from Cancelled upload
        this.spinnerService.hide();
      });
  }
}
